import type { EventHandler } from '@/modules/events/contracts';
import type { ErpEventName, ErpEventPayloadMap } from '@/modules/events/types';

/**
 * Bus de eventos **in-process** (misma pestaña / mismo runtime JS).
 *
 * NO es una cola distribuida ni durable. Si el handler falla después de persistir
 * datos, no hay reintento automático. Ver docs/EVENT_ARCHITECTURE.md.
 */
const handlersByEvent = new Map<ErpEventName, Set<EventHandler<ErpEventName>>>();

const processedIdempotency = new Set<string>();
const IDEMPOTENCY_MAX = 200;

export function registerHandler<T extends ErpEventName>(
  eventName: T,
  handler: EventHandler<T>,
): () => void {
  let set = handlersByEvent.get(eventName);
  if (!set) {
    set = new Set();
    handlersByEvent.set(eventName, set);
  }
  set.add(handler as EventHandler<ErpEventName>);

  return () => {
    set?.delete(handler as EventHandler<ErpEventName>);
  };
}

export async function publishEvent<T extends ErpEventName>(
  eventName: T,
  payload: ErpEventPayloadMap[T],
  options?: { idempotencyKey?: string },
): Promise<void> {
  if (options?.idempotencyKey) {
    if (processedIdempotency.has(options.idempotencyKey)) return;
    processedIdempotency.add(options.idempotencyKey);
    if (processedIdempotency.size > IDEMPOTENCY_MAX) {
      const first = processedIdempotency.values().next().value;
      if (first) processedIdempotency.delete(first);
    }
  }

  const handlers = handlersByEvent.get(eventName);
  if (!handlers?.size) return;

  for (const handler of handlers) {
    try {
      await handler(payload);
    } catch (err) {
      console.error(`[eventBus] handler failed for ${eventName}`, err);
    }
  }
}

export function clearEventHandlersForTests(): void {
  handlersByEvent.clear();
  processedIdempotency.clear();
}
